import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { MdEmail } from "react-icons/md";
import axios from "axios";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "../../Assets/logo/image.png";
import imgs from "../../Assets/login.jpg";
import BtnLoader from "../../Components/Common/BtnLoader";

const Login = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [isLoading, setIsLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);

  const sendOtp = async () => {
    const email = document.getElementById("email")?.value;

    if (!email) {
      toast.error("Please enter email first");
      return;
    }

    try {
      setIsLoading(true);

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/send-login-otp`,
        { email },
      );

      // Send the OTP via EmailJS from the browser
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          email: email,
          passcode: data.otp,
          time: new Date(Date.now() + 10 * 60 * 1000).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          company_name: "Nexgen",
          year: new Date().getFullYear(),
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
      );

      setOtp("");
      setOtpSent(true);
      toast.success("OTP sent to your email");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.text || "Failed to send OTP",
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Helper to persist session + navigate
  const handleLoginSuccess = (data) => {
    localStorage.setItem("Nexgen-Admin-Data", JSON.stringify(data?.user));
    localStorage.setItem("Nexgen-Token", data?.token);
    toast.success("Admin login successful");
    navigate("/admin/dashboard");
  };

  const onSubmit = async (credentials) => {
    if (!otp) {
      toast.error("Please enter the OTP sent to your email");
      return;
    }

    try {
      setIsLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admin/login-otp`,
        {
          email: credentials.email,
          otp,
        },
      );

      handleLoginSuccess(response?.data);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-400 p-4">
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={true}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <div className="relative z-10 flex w-full max-w-4xl overflow-hidden rounded-xl shadow-2xl bg-white md:flex-row flex-col-reverse">
        {/* Left Section */}
        <div className="w-full p-8 md:w-1/2 flex flex-col justify-between">
          <div>
            <div className="mb-4 flex justify-center">
              <img src={logo} alt="Logo" className="w-40 " />
            </div>
            <h1 className="text-2xl tracking-wide font-bold text-gray-800 text-center uppercase">
              Nexgen
            </h1>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value:
                          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                        message: "Invalid email address",
                      },
                    })}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    placeholder="Enter your email"
                  />
                  <MdEmail
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                </div>
                {errors.email && (
                  <p className="text-xs text-red-500">{errors.email.message}</p>
                )}
              </div>

              {/* OTP Section */}
              <div className="space-y-3 mt-4">
                {!otpSent ? (
                  <button
                    type="button"
                    onClick={sendOtp}
                    className="w-full rounded-lg bg-green-600 h-11 text-white font-medium"
                  >
                    Send OTP
                  </button>
                ) : (
                  <>
                    <p className="text-xs text-gray-500">
                      Check your email for the OTP we sent you.
                    </p>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      placeholder="Enter 4 Digit OTP"
                      maxLength={4}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />

                    <button
                      type="button"
                      onClick={sendOtp}
                      className="text-sm text-blue-600"
                    >
                      Resend OTP
                    </button>
                  </>
                )}
              </div>

              {isLoading ? (
                <button
                  type="submit"
                  className="w-full rounded-lg bg-blue-600 flex justify-center items-center h-11 mt-6"
                >
                  <BtnLoader />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!otpSent}
                  className="w-full rounded-lg bg-blue-600 h-11 text-white font-medium transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Verify OTP & Login
                </button>
              )}
            </form>
          </div>
          <p className="mt-6 text-center text-xs text-gray-500">
            © {currentYear} All Rights Reserved
          </p>
        </div>

        {/* Right Section - Image */}
        <div className="hidden md:block md:w-1/2 bg-blue-600">
          <img src={imgs} alt="Login" className="h-full w-full object-cover" />
        </div>
      </div>
    </div>
  );
};

export default Login;

// import React, { useState } from "react";
// import { useForm } from "react-hook-form";
// import { useNavigate } from "react-router-dom";
// import { MdEmail } from "react-icons/md";
// import axios from "axios";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import logo from "../../Assets/logo/image.png";
// import imgs from "../../Assets/login.jpg";
// import BtnLoader from "../../Components/Common/BtnLoader";

// const Login = () => {
//   const {
//     register,
//     handleSubmit,
//     formState: { errors },
//   } = useForm();
//   const navigate = useNavigate();
//   const currentYear = new Date().getFullYear();
//   const [isLoading, setIsLoading] = useState(false);
//   const [otp, setOtp] = useState("");
//   const [otpSent, setOtpSent] = useState(false);

//   const sendOtp = async () => {
//     const email = document.getElementById("email")?.value;

//     if (!email) {
//       toast.error("Please enter email first");
//       return;
//     }

//     try {
//       setIsLoading(true);

//       const response = await axios.post(
//         `${process.env.REACT_APP_API_URL}/admin/send-login-otp`,
//         { email },
//       );

//       // OTP is returned directly in the response instead of being emailed
//       setOtp(response?.data?.otp || "");
//       setOtpSent(true);
//       toast.success(`Your OTP is ${response?.data?.otp}`);
//     } catch (error) {
//       toast.error(error?.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   // Helper to persist session + navigate
//   const handleLoginSuccess = (data) => {
//     localStorage.setItem("Nexgen-Admin-Data", JSON.stringify(data?.user));
//     localStorage.setItem("Nexgen-Token", data?.token);
//     toast.success("Admin login successful");
//     navigate("/admin/dashboard");
//   };

//   const onSubmit = async (credentials) => {
//     try {
//       setIsLoading(true);

//       const response = await axios.post(
//         `${process.env.REACT_APP_API_URL}/admin/login-otp`,
//         {
//           email: credentials.email,
//           otp,
//         },
//       );

//       handleLoginSuccess(response?.data);
//     } catch (error) {
//       toast.error(error?.response?.data?.message || "Login failed");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   return (
//     <div className="flex h-screen w-full items-center justify-center bg-gradient-to-br from-blue-500 to-cyan-400 p-4">
//       <ToastContainer
//         position="top-center"
//         autoClose={3000}
//         hideProgressBar={true}
//         newestOnTop
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//       />
//       <div className="relative z-10 flex w-full max-w-4xl overflow-hidden rounded-xl shadow-2xl bg-white md:flex-row flex-col-reverse">
//         {/* Left Section */}
//         <div className="w-full p-8 md:w-1/2 flex flex-col justify-between">
//           <div>
//             <div className="mb-4 flex justify-center">
//               <img src={logo} alt="Logo" className="w-40 " />
//             </div>
//             <h1 className="text-2xl tracking-wide font-bold text-gray-800 text-center uppercase">
//               Nexgen
//             </h1>

//             <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
//               {/* Email Input */}
//               <div className="space-y-2">
//                 <label
//                   htmlFor="email"
//                   className="text-sm font-medium text-gray-700"
//                 >
//                   Email
//                 </label>
//                 <div className="relative">
//                   <input
//                     id="email"
//                     type="email"
//                     {...register("email", {
//                       required: "Email is required",
//                       pattern: {
//                         value:
//                           /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
//                         message: "Invalid email address",
//                       },
//                     })}
//                     className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
//                     placeholder="Enter your email"
//                   />
//                   <MdEmail
//                     className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
//                     size={20}
//                   />
//                 </div>
//                 {errors.email && (
//                   <p className="text-xs text-red-500">{errors.email.message}</p>
//                 )}
//               </div>

//               {/* OTP Section */}
//               <div className="space-y-3 mt-4">
//                 {!otpSent ? (
//                   <button
//                     type="button"
//                     onClick={sendOtp}
//                     className="w-full rounded-lg bg-green-600 h-11 text-white font-medium"
//                   >
//                     Send OTP
//                   </button>
//                 ) : (
//                   <>
//                     <input
//                       type="text"
//                       value={otp}
//                       onChange={(e) => setOtp(e.target.value)}
//                       placeholder="Enter 4 Digit OTP"
//                       maxLength={4}
//                       className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
//                     />

//                     <button
//                       type="button"
//                       onClick={sendOtp}
//                       className="text-sm text-blue-600"
//                     >
//                       Resend OTP
//                     </button>
//                   </>
//                 )}
//               </div>

//               {isLoading ? (
//                 <button
//                   type="submit"
//                   className="w-full rounded-lg bg-blue-600 flex justify-center items-center h-11 mt-6"
//                 >
//                   <BtnLoader />
//                 </button>
//               ) : (
//                 <button
//                   type="submit"
//                   disabled={!otpSent}
//                   className="w-full rounded-lg bg-blue-600 h-11 text-white font-medium transition hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
//                 >
//                   Verify OTP & Login
//                 </button>
//               )}
//             </form>
//           </div>
//           <p className="mt-6 text-center text-xs text-gray-500">
//             © {currentYear} All Rights Reserved
//           </p>
//         </div>

//         {/* Right Section - Image */}
//         <div className="hidden md:block md:w-1/2 bg-blue-600">
//           <img src={imgs} alt="Login" className="h-full w-full object-cover" />
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Login;
